import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：分頁列。
 *
 * 原系統對應：DataTables 設定於 `js-my/common.js`
 *   `:827` `sPaginationType: "full_numbers"` → 首頁／上一頁／頁碼／下一頁／末頁 五段式
 *   `:828` `dom: 'Bfrtip'` 中的 `p` 就是這一列（位置在表格下方）
 *   `:807-812` 文字：
 *          `"first": "第一頁"`、`"previous": "上一頁"`、`"next": "下一頁"`、`"last": "最後一頁"`
 *
 * ponytail: 分頁計算是三行算術，不引函式庫（§3.2 也禁止引入 DataTables／jQuery）。
 * 頁碼視窗固定寬度、超出以省略號表示，不模擬 DataTables 的完整視窗演算法 ——
 * 雛形只要「看起來是 full_numbers、點得到、回呼正確」。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface PaginationProps {
  uiId: string;
  /** 目前頁碼，1 起算。 */
  page: number;
  /** 總頁數。0 時整個元件不渲染（對應沒有資料的表格）。 */
  pageCount: number;
  onChange(page: number): void;
  /** 中間顯示幾個頁碼按鈕（含目前頁）。 */
  windowSize?: number;
  disabled?: boolean;
}

/** 回傳要顯示的頁碼陣列（1 起算），必要時在頭尾補 null 代表省略號。 */
export function pageWindow(
  page: number,
  pageCount: number,
  windowSize: number,
): readonly (number | null)[] {
  if (pageCount <= 0) return [];
  const size = Math.max(1, Math.min(windowSize, pageCount));
  let start = Math.max(1, page - Math.floor(size / 2));
  const end = Math.min(pageCount, start + size - 1);
  start = Math.max(1, end - size + 1);

  const result: (number | null)[] = [];
  if (start > 1) result.push(1, start > 2 ? null : 2);
  for (let n = start; n <= end; n += 1) {
    if (!result.includes(n)) result.push(n);
  }
  if (end < pageCount) {
    if (end < pageCount - 1) result.push(null);
    result.push(pageCount);
  }
  return result;
}

const STEP_BUTTON = 'border border-border px-2 py-1 text-sm disabled:opacity-40';

export function Pagination({
  uiId,
  page,
  pageCount,
  onChange,
  windowSize = 5,
  disabled = false,
}: PaginationProps) {
  if (pageCount <= 0) return null;

  const atFirst = disabled || page <= 1;
  const atLast = disabled || page >= pageCount;

  return (
    <nav data-ui-id={uiId} aria-label={t('a11y.pagination')} className="flex flex-wrap gap-1">
      <button
        type="button"
        data-ui-id={`${uiId}-first`}
        className={STEP_BUTTON}
        disabled={atFirst}
        onClick={() => onChange(1)}
      >
        {t('page.first')}
      </button>
      <button
        type="button"
        data-ui-id={`${uiId}-previous`}
        className={STEP_BUTTON}
        disabled={atFirst}
        onClick={() => onChange(page - 1)}
      >
        {t('page.previous')}
      </button>
      {pageWindow(page, pageCount, windowSize).map((n, index) =>
        n === null ? (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: 省略號沒有其他穩定識別，位置本身就是身分
            key={`gap-${index}`}
            data-ui-id={`${uiId}-gap`}
            aria-hidden="true"
            className="px-2 py-1 text-sm"
          >
            …
          </span>
        ) : (
          <button
            key={n}
            type="button"
            data-ui-id={`${uiId}-page-${n}`}
            aria-current={n === page ? 'page' : undefined}
            disabled={disabled}
            className={
              n === page
                ? 'border border-brand bg-brand px-2 py-1 text-sm text-surface'
                : STEP_BUTTON
            }
            onClick={() => onChange(n)}
          >
            {n}
          </button>
        ),
      )}
      <button
        type="button"
        data-ui-id={`${uiId}-next`}
        className={STEP_BUTTON}
        disabled={atLast}
        onClick={() => onChange(page + 1)}
      >
        {t('page.next')}
      </button>
      <button
        type="button"
        data-ui-id={`${uiId}-last`}
        className={STEP_BUTTON}
        disabled={atLast}
        onClick={() => onChange(pageCount)}
      >
        {t('page.last')}
      </button>
    </nav>
  );
}
